<script lang="ts">
	import { isLoading } from 'svelte-i18n';
	// attach the mobile functions to the window object on app start by importing this file
	import { mobile } from './services/mobile-rework';

	import { setUpI18n, setDictionary } from './services/i18n';
	import Router from 'svelte-spa-router';
	import type { RouteDetail } from 'svelte-spa-router';
	import { replace } from 'svelte-spa-router';
	import { wrap } from 'svelte-spa-router/wrap';
	import { user_settings } from 'src/stores/account/user';
	import { initializing_language } from 'src/stores/session/preferences';
	import { Theme, ToastContainer } from '@Shelly/shelly-design-system';
	import { afterLoginParams } from 'src/stores/session/after-login-route';
	import { checkUser } from './helpers/user_helpers';
	import { onMount } from 'svelte';
	import { initSentry } from './services/sentry';
	import {
		getNativeInformation,
		initNative,
		initServerConnection,
		initWeb,
		prepareLocalStorageForOfflineMode,
		setNativeInformation,
	} from './services/init/app-initialization';

	import LoginLayout from './routes/LoginLayout.svelte';
	import LoginLayoutV2Wrapper from './routes/LoginLayoutV2.svelte';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	import { get } from 'svelte/store';
	import PrivacyPolicy from './routes/LegalPages/PrivacyPolicy.svelte';
	import TermsAndConditions from './routes/LegalPages/TermsAndConditions.svelte';
	import DataAct from './routes/LegalPages/DataAct.svelte';
	import Changelog from './routes/Changelog.svelte';
	import MainLayoutManager from './routes/MainLayoutManager.svelte';
	import DevTrigger from './components/Dev/DevTrigger.svelte';
	import SafeAreaDebug from './components/Dev/SafeAreaDebug.svelte';

	const nativeInformation = getNativeInformation();
	const language = $user_settings.lang || $initializing_language || 'en';
	const PublicLayout = get(showNewLayout) ? LoginLayoutV2Wrapper : LoginLayout;
	const routes = {
		'/login': wrap({
			component: PublicLayout,
			conditions: [
				() => {
					return !checkUser();
				},
			],
		}),
		'/signup': PublicLayout,
		'/request-password-reset': PublicLayout,
		'/password-reset': PublicLayout,
		'/request-account-delete': PublicLayout,
		'/account-delete': PublicLayout,
		'/email-verification': PublicLayout,
		'/change-email': PublicLayout,
		'/privacy-policy': PrivacyPolicy,
		'/terms-and-conditions': TermsAndConditions,
		'/data-act': DataAct,
		'/changelog': Changelog,
		'/fast-login': PublicLayout,
		'/installer-login': PublicLayout,
		'/register_subaccount/:token/:email': PublicLayout,
		'/*': wrap({
			component: MainLayoutManager,
			conditions: [
				() => {
					return checkUser();
				},
			],
		}),
	};

	/**
	 * Check if there is a location in the detail object
	 * @param event
	 */
	const isRouteDetail = (e: CustomEvent<RouteDetail>): e is CustomEvent<RouteDetail> => {
		return e.detail && typeof e.detail === 'object' && e.detail?.location != '/';
	};

	const backToLogin = (e) => {
		if (e.detail.location.includes('login')) {
			console.log('[Login] Redirect home');
			replace('/');
			return;
		}

		if (isRouteDetail(e)) {
			console.log('[Login] Setting redirect URL after login:', e.detail);
			afterLoginParams.set(e.detail);
		}

		console.log('[Login] Redirecting to login');
		replace('/login');
	};

	const routeEvent = (e) => {
		console.warn('Caught event routeEvent', e.detail);
	};

	const isIOSDevice = () => {
		return (
			/iPad|iPhone|iPod/.test(navigator.userAgent) ||
			(navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
		);
	};

	onMount(() => {
		setDictionary();
		initSentry();
		setUpI18n(language);
		setNativeInformation(nativeInformation);
		prepareLocalStorageForOfflineMode();

		if (!nativeInformation.isApp) {
			initWeb();
		} else {
			initNative(nativeInformation.os);
		}

		initServerConnection(nativeInformation.os);
		if (!isIOSDevice()) return;

		const root = document.documentElement;
		const aiModalClass = 'ai-scene-modal-open';
		let viewportListenersAttached = false;

		/**
		 * Keyboard-closed baseline height. Refreshed only while no field is
		 * focused so it captures the true full height and isn't corrupted by a
		 * viewport that is already shrunk by the keyboard (FE-1764).
		 */
		let fullHeight = window.innerHeight;
		let touchStartY = 0;
		let touchStartX = 0;

		/** Whether the on-screen keyboard is currently open on a non-AI page. */
		const isKeyboardOpenOutsideAi = () => {
			const active = document.activeElement;
			const isInputFocused =
				active instanceof HTMLInputElement ||
				active instanceof HTMLTextAreaElement ||
				active instanceof HTMLSelectElement;

			if (!isInputFocused) {
				fullHeight = Math.max(fullHeight, window.innerHeight);
			}

			const visibleHeight = Math.round(window.visualViewport?.height ?? window.innerHeight);
			const keyboardHeight = Math.max(0, fullHeight - Math.min(visibleHeight, window.innerHeight));
			return keyboardHeight > 0 && !document.body.classList.contains(aiModalClass);
		};

		/**
		 * Reports whether `el` can still scroll vertically in `direction`.
		 * `direction > 0` is an upward swipe (towards the top); `direction < 0`
		 * is a downward swipe (towards the bottom).
		 */
		const canScrollInDirection = (el: Element, direction: number) => {
			if (el.scrollHeight <= el.clientHeight) return false;
			const atTop = el.scrollTop <= 0;
			const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 1;
			if (direction > 0 && !atTop) return true;
			if (direction < 0 && !atBottom) return true;
			return false;
		};

		/**
		 * Finds the nearest ancestor that can scroll vertically in `direction`.
		 * Falls back to the document scrolling element so pages that scroll the
		 * document itself (e.g. login, which has no overflow container) aren't
		 * frozen by the touch handler.
		 */
		const hasScrollableAncestor = (target: EventTarget | null, direction: number) => {
			let el = target instanceof Element ? target : null;
			while (el && el !== document.body) {
				const overflowY = getComputedStyle(el).overflowY;
				const scrolls = overflowY === 'auto' || overflowY === 'scroll';
				if (scrolls && canScrollInDirection(el, direction)) return true;
				el = el.parentElement;
			}

			const scrollingElement = document.scrollingElement ?? document.documentElement;
			return scrollingElement ? canScrollInDirection(scrollingElement, direction) : false;
		};

		const onTouchStart = (event: TouchEvent) => {
			touchStartY = event.touches[0]?.clientY ?? 0;
			touchStartX = event.touches[0]?.clientX ?? 0;
		};

		/**
		 * Blocks the page/WKWebView from scrolling or rubber-banding as a whole
		 * while the keyboard is open and the gesture isn't inside a list that can
		 * actually scroll. This is the only reliable way to stop the native iOS
		 * scroll (CSS overflow/position tricks don't). Layout is untouched.
		 */
		const onTouchMove = (event: TouchEvent) => {
			if (event.touches.length > 1 || !isKeyboardOpenOutsideAi()) return;

			const touch = event.touches[0];
			const deltaY = touch.clientY - touchStartY;
			const deltaX = touch.clientX - touchStartX;
			if (Math.abs(deltaY) <= Math.abs(deltaX)) return;

			if (!hasScrollableAncestor(event.target, deltaY) && event.cancelable) {
				event.preventDefault();
			}
		};

		const applyViewportHeight = () => {
			const viewportHeight = Math.round(window.visualViewport?.height ?? window.innerHeight);
			const viewportOffsetTop = Math.round(window.visualViewport?.offsetTop ?? 0);
			root.style.setProperty('--ios-visual-viewport-height', `${viewportHeight}px`);
			root.style.setProperty('--ios-visual-viewport-offset-top', `${viewportOffsetTop}px`);
		};

		const teardownAiViewportLock = () => {
			if (viewportListenersAttached) {
				window.visualViewport?.removeEventListener('resize', applyViewportHeight);
				window.visualViewport?.removeEventListener('scroll', applyViewportHeight);
				window.removeEventListener('resize', applyViewportHeight);
				viewportListenersAttached = false;
			}
			root.classList.remove('ios-vv-height-lock');
			root.style.removeProperty('--ios-visual-viewport-height');
			root.style.removeProperty('--ios-visual-viewport-offset-top');
		};

		const setupAiViewportLock = () => {
			root.classList.add('ios-vv-height-lock');
			applyViewportHeight();
			if (!viewportListenersAttached) {
				window.visualViewport?.addEventListener('resize', applyViewportHeight);
				window.visualViewport?.addEventListener('scroll', applyViewportHeight);
				window.addEventListener('resize', applyViewportHeight);
				viewportListenersAttached = true;
			}
		};

		const syncAiViewportLock = () => {
			if (document.body.classList.contains(aiModalClass)) {
				setupAiViewportLock();
				return;
			}
			teardownAiViewportLock();
		};

		const bodyClassObserver = new MutationObserver((records) => {
			for (const record of records) {
				if (record.type === 'attributes' && record.attributeName === 'class') {
					syncAiViewportLock();
					return;
				}
			}
		});

		syncAiViewportLock();
		bodyClassObserver.observe(document.body, {
			attributes: true,
			attributeFilter: ['class'],
		});

		document.addEventListener('touchstart', onTouchStart, { passive: true });
		document.addEventListener('touchmove', onTouchMove, { passive: false });

		return () => {
			bodyClassObserver.disconnect();
			teardownAiViewportLock();
			document.removeEventListener('touchstart', onTouchStart);
			document.removeEventListener('touchmove', onTouchMove);
		};
	});
</script>

<ToastContainer />

<Theme>
	{#if !$isLoading}
		<Router {routes} on:conditionsFailed={backToLogin} on:routeEvent={routeEvent} />
	{/if}
	<DevTrigger />
	<SafeAreaDebug />
</Theme>
